import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import { useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ConfigContext } from "../../utils/ConfigContext";
import { checkLayerForFeatureTable } from "../../utils/featureTableUtils";

const useFeatureTable = () => {
  const { config } = useContext(ConfigContext);
  const [layerExists, setLayerExists] = useState(false);
  const [_count, setCount] = useState(0);
  const tableLayers = useRef({});
  const count = useRef(0);

  const activeModule = useSelector((state) => state.activeModule);
  const view = useSelector((state) => state.mapViewReducer.view);

  const handles = useRef({
    change: undefined,
    watchLayers: [],
  });

  const isShownInTable = (layer, visible) => {
    const { layers = [], optionalLayers = [] } =
      config.modules[activeModule] || {};
    const isVisible =
      layers.includes(layer.layerConfig?.alias) ||
      optionalLayers.includes(layer.layerConfig?.alias) ||
      visible;

    const isShown = checkLayerForFeatureTable(layer, false);
    tableLayers.current[layer.id] = isVisible && !!isShown;
    count.current += 1;
    setCount(count.current);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const featureTableLayer =
        Object.values(tableLayers.current).filter((isShown) => isShown).length >
        0;

      if (featureTableLayer && !layerExists) {
        setLayerExists(true);
      }

      if (!featureTableLayer && layerExists) {
        setLayerExists(false);
      }
    }, 250);

    return () => {
      clearTimeout(timer);
    };
  }, [_count]);

  useEffect(() => {
    tableLayers.current = {};
    setCount(0);
    setLayerExists(false);
  }, [activeModule]);

  useEffect(() => {
    if (!view) return;

    const timer = setTimeout(() => {
      view.map.layers.on("change", (event) => {
        if (event.added)
          event.added.forEach((layer) => {
            isShownInTable(layer, layer.visible);
            const visibleHandler = reactiveUtils.watch(
              () => layer.visible,
              (visible) => {
                isShownInTable(layer, visible);
              }
            );
            handles.current.watchLayers.push(visibleHandler);
          });
      });

      view.map.layers.forEach((layer) => {
        isShownInTable(layer, layer.visible);
        const visibleHandler = reactiveUtils.watch(
          () => layer.visible,
          (visible) => {
            isShownInTable(layer, visible);
          }
        );
        handles.current.watchLayers.push(visibleHandler);
      });
    }, 250);

    return () => {
      clearTimeout(timer);
      handles.current.watchLayers.map((h) => h.remove());
    };
  }, [activeModule, config, view]);

  return {
    count: _count,
    layerIds: tableLayers.current,
    layerExists,
    setLayerExists,
  };
};

export default useFeatureTable;
